import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, X } from "lucide-react";
import { Api } from "../lib/api.js";
import { linkForItem } from "../lib/linkForItem.js";

/**
 * "More like this" — fan-out panel. The viewer clicks the button on a Watch
 * page; we render the source item's poster as the anchor and radiate
 * outwards into one mini-row per characteristic axis (genre, decade,
 * genre+decade, themed keyword).
 *
 * Each row is its own horizontal scroller; click any pick to jump to that
 * Watch page.
 */
export function MoreLikeThis({
  itemId,
  profileId,
}: {
  itemId: string;
  profileId: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  const q = useQuery({
    queryKey: ["like-this", itemId, profileId],
    queryFn: () => Api.likeThis(itemId, profileId),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="pill-ghost"
        title="Fan out into recommendations by characteristic"
      >
        <Sparkles size={14} /> More like this
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center px-4 py-6 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink/90 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative z-10 card w-full max-w-5xl my-6 overflow-hidden">
            <header className="px-6 py-4 border-b border-hairline flex items-center justify-between">
              <div className="flex items-baseline gap-3">
                <Sparkles className="text-goldHi" size={18} />
                <div>
                  <div className="section-label text-gold">— MORE LIKE THIS —</div>
                  {q.data?.source && (
                    <h2 className="font-display text-ivory text-xl mt-0.5">
                      Branches from <span className="text-goldHi">{q.data.source.title}</span>
                    </h2>
                  )}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="pill-ghost p-2" aria-label="Close">
                <X size={18} />
              </button>
            </header>

            <div className="px-6 py-5 max-h-[80vh] overflow-y-auto">
              {q.isLoading && (
                <div className="space-y-6">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i}>
                      <div className="skeleton h-3 w-48 mb-3" />
                      <div className="flex gap-3 overflow-hidden">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <div key={j} className="skeleton w-[150px] aspect-[2/3]" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {q.data && (
                <div className="grid md:grid-cols-[160px_1fr] gap-6">
                  {/* Source anchor poster */}
                  <div className="hidden md:block">
                    {q.data.source.thumbnail_url ? (
                      <img
                        src={q.data.source.thumbnail_url}
                        alt=""
                        className="w-full rounded-lg border border-gold/40 shadow-lg"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-full aspect-[2/3] bg-elev rounded-lg" />
                    )}
                    {q.data.source.genres.length > 0 && (
                      <div className="mt-3 text-parchment text-xs leading-relaxed">
                        <div className="section-label">Anchor traits</div>
                        <ul className="mt-1 space-y-0.5">
                          {q.data.source.genres.slice(0, 4).map((g) => (
                            <li key={g} className="text-ivory">— {g}</li>
                          ))}
                          {q.data.source.year && (
                            <li className="text-ivory">— {q.data.source.year}</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Axis rows */}
                  <div className="space-y-7 min-w-0">
                    {q.data.axes.length === 0 ? (
                      <div className="card-elev p-5 text-parchment text-sm">
                        Not enough TMDB-matched neighbours in your catalog yet —
                        finish the backfill, then try again.
                      </div>
                    ) : (
                      q.data.axes.map((ax) => (
                        <AxisRow key={ax.kind + ":" + ax.label} axis={ax} onJump={() => setOpen(false)} />
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface AxisItem {
  id: string;
  kind: string;
  title: string;
  category?: string | null;
  thumbnail_url: string | null;
  backdrop_url: string | null;
  tmdb_rating: number | null;
  release_year: number | null;
  episode_count?: number;
}

function AxisRow({
  axis,
  onJump,
}: {
  axis: { kind: string; label: string; items: AxisItem[] };
  onJump: () => void;
}): JSX.Element {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-2">
        <div className="font-display text-goldHi text-sm tracking-smallcaps">
          ❖ {axis.label}
        </div>
        <div className="text-parchment/60 text-[11px]">{axis.items.length} picks</div>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
        {axis.items.map((it) => (
          <Link
            key={it.id}
            to={linkForItem({ id: it.id, kind: it.kind, category: it.category ?? null })}
            onClick={onJump}
            className="min-w-[150px] snap-start card group overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi hover:scale-[1.04] transition-transform duration-200 ease-spring"
          >
            <div className="aspect-[2/3] bg-elev relative">
              {it.thumbnail_url && (
                <img
                  src={it.thumbnail_url}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover"
                />
              )}
              {it.tmdb_rating !== null && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] bg-ink/85 border border-gold/40 font-display tabular-nums text-goldHi">
                  ★ {Math.round(it.tmdb_rating * 10) / 10}
                </span>
              )}
              {it.release_year && (
                <span className="absolute top-1 right-1 px-1 py-0.5 rounded text-[9px] bg-ink/85 border border-hairline font-display tabular-nums text-parchment">
                  {it.release_year}
                </span>
              )}
            </div>
            <div className="p-2">
              <div className="text-ivory text-xs leading-tight line-clamp-2 min-h-[2.2em]">
                {it.title}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
